// Loads what the Schedules simple view's row actions show, for every row at
// once: orders & follow-ups, unread patient texts, and related tasks.
//
// Each source is ONE request per schema (or one in total), never one per row,
// and is matched to rows locally (rules in utils/scheduleRowActions.js):
//   orders — fetchOrders per schema, with the backend's storage-walking date
//            repair skipped (the chart's Orders modal still runs it);
//   texts  — the unreviewed inbound SMS of each schema, counted per phone;
//   tasks  — every task made from each patient's charts (any visit), whoever
//            assigned it or holds it: one lookup per schema for all of that
//            schema's patients on the page.
// A source that fails after loading keeps its last good data rather than
// blanking every row's badge.
import { useEffect, useMemo, useState } from "react";
import { getSupabase, supabaseSchema } from "../components/supabaseClient";
import { fetchOrders } from "../services/clinicComplianceService";
import { fetchTasksForPatients } from "../services/messagingService";
import { onWorkItemsChanged } from "../utils/workItemsBus";
import { clientForSchema } from "../utils/reportsPathSchema";
import {
  buildOrderIndex,
  countUnreadByPhone,
  orderItemsForRow,
  orderSchemasForRows,
  patientNamesBySchema,
  rowPhonesNormalized,
  rowSchemaOf,
  tasksForRow,
  unreadForRow,
} from "../utils/scheduleRowActions";

// Unreviewed inbound texts read per schema. Generous: the badge only needs the
// `from` column, and these are marked reviewed as soon as someone opens them.
export const SMS_UNREAD_LIMIT = 2000;
// New patient replies show up within a minute while the page is visible.
export const SMS_POLL_MS = 60000;

const IDLE = "idle";
const LOADING = "loading";
const READY = "ready";
const ERROR = "error";

// A reload that fails after a good load keeps showing the good load.
const afterFailure = (prev) => (prev.status === READY ? READY : ERROR);

export default function useScheduleRowActionData({
  rows,
  fallbackSchema,
  orders: ordersOn = false,
  messages: messagesOn = false,
  tasks: tasksOn = false,
}) {
  const fallback = fallbackSchema || supabaseSchema || "public";

  // Stable keys, so a new `rows` array with the same people refetches nothing.
  const orderSchemasKey = ordersOn ? orderSchemasForRows(rows, fallback).join("|") : "";
  const smsSchemasKey = messagesOn
    ? [...new Set((rows || []).filter((r) => rowPhonesNormalized(r).size > 0).map((r) => rowSchemaOf(r, fallback)))]
        .sort()
        .join("|")
    : "";
  const taskLookupKey = tasksOn ? JSON.stringify(patientNamesBySchema(rows, fallback)) : "";

  const [ordersNonce, setOrdersNonce] = useState(0);
  const [smsNonce, setSmsNonce] = useState(0);
  const [tasksNonce, setTasksNonce] = useState(0);

  // ── Orders ────────────────────────────────────────────────────────────────
  const [orderState, setOrderState] = useState({ status: IDLE, groupsBySchema: {} });
  useEffect(() => {
    if (!orderSchemasKey) {
      setOrderState({ status: IDLE, groupsBySchema: {} });
      return undefined;
    }
    let cancelled = false;
    const schemas = orderSchemasKey.split("|");
    setOrderState((prev) => (prev.status === READY ? prev : { ...prev, status: LOADING }));
    Promise.all(schemas.map((schema) => fetchOrders({ schema, skipDateRepair: true }))).then((results) => {
      if (cancelled) return;
      setOrderState((prev) => {
        // fetchOrders never throws; a failed schema comes back flagged `_error`.
        if (results.every((r) => !r || r._error)) return { ...prev, status: afterFailure(prev) };
        const groupsBySchema = {};
        results.forEach((r, i) => {
          groupsBySchema[schemas[i]] = !r || r._error ? prev.groupsBySchema[schemas[i]] || [] : r.patients || [];
        });
        return { status: READY, groupsBySchema };
      });
    });
    return () => {
      cancelled = true;
    };
  }, [orderSchemasKey, ordersNonce]);

  const orderIndex = useMemo(() => {
    const out = {};
    Object.entries(orderState.groupsBySchema).forEach(([schema, groups]) => {
      out[schema] = buildOrderIndex(groups);
    });
    return out;
  }, [orderState.groupsBySchema]);

  // ── Unread patient texts ─────────────────────────────────────────────────
  const [smsState, setSmsState] = useState({ status: IDLE, bySchema: {} });
  useEffect(() => {
    if (!smsSchemasKey) {
      setSmsState({ status: IDLE, bySchema: {} });
      return undefined;
    }
    let cancelled = false;
    const schemas = smsSchemasKey.split("|");
    setSmsState((prev) => (prev.status === READY ? prev : { ...prev, status: LOADING }));
    (async () => {
      let supabase = null;
      try {
        supabase = await getSupabase();
      } catch (err) {
        console.warn("[row actions] no Supabase client for unread texts:", err);
      }
      const results = await Promise.all(
        schemas.map(async (schema) => {
          if (!supabase) return null;
          try {
            // SMS is per schema: a fan-out row's texts live in ITS schema.
            const { data, error } = await clientForSchema(supabase, schema, supabaseSchema)
              .from("SMS")
              .select("from")
              .eq("direction", "inbound")
              .eq("reviewed", false)
              .order("created_at", { ascending: false })
              .limit(SMS_UNREAD_LIMIT);
            if (error) throw error;
            return countUnreadByPhone(data);
          } catch (err) {
            console.warn(`[row actions] unread texts for "${schema}" failed:`, err?.message || err);
            return null;
          }
        }),
      );
      if (cancelled) return;
      setSmsState((prev) => {
        if (results.every((r) => r === null)) return { ...prev, status: afterFailure(prev) };
        const bySchema = {};
        results.forEach((counts, i) => {
          bySchema[schemas[i]] = counts || prev.bySchema[schemas[i]] || {};
        });
        return { status: READY, bySchema };
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [smsSchemasKey, smsNonce]);

  useEffect(() => {
    if (!smsSchemasKey) return undefined;
    const timer = setInterval(() => {
      if (typeof document === "undefined" || document.visibilityState !== "hidden") {
        setSmsNonce((n) => n + 1);
      }
    }, SMS_POLL_MS);
    return () => clearInterval(timer);
  }, [smsSchemasKey]);

  // ── Tasks ─────────────────────────────────────────────────────────────────
  const [taskState, setTaskState] = useState({ status: IDLE, bySchema: {} });
  useEffect(() => {
    if (!taskLookupKey) {
      setTaskState({ status: IDLE, bySchema: {} });
      return undefined;
    }
    const lookups = Object.entries(JSON.parse(taskLookupKey));
    if (lookups.length === 0) {
      // No row names a patient (a class or client schedule): nothing to find.
      setTaskState({ status: READY, bySchema: {} });
      return undefined;
    }
    let cancelled = false;
    setTaskState((prev) => (prev.status === READY ? prev : { ...prev, status: LOADING }));
    Promise.allSettled(lookups.map(([schema, names]) => fetchTasksForPatients(schema, names))).then(
      (results) => {
        if (cancelled) return;
        results.forEach((r, i) => {
          if (r.status === "rejected") {
            console.warn(`[row actions] tasks for "${lookups[i][0]}" failed:`, r.reason?.message || r.reason);
          }
        });
        setTaskState((prev) => {
          if (results.every((r) => r.status === "rejected")) return { ...prev, status: afterFailure(prev) };
          const bySchema = {};
          results.forEach((r, i) => {
            const schema = lookups[i][0];
            bySchema[schema] = r.status === "fulfilled" ? r.value?.tasks || [] : prev.bySchema[schema] || [];
          });
          return { status: READY, bySchema };
        });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [taskLookupKey, tasksNonce]);

  // Any open view (Messages → Tasks, the Orders modal, Compliance) that changes
  // a task or an order reloads both. A comment changes neither.
  useEffect(
    () =>
      onWorkItemsChanged((e) => {
        if (e?.detail?.action === "comment") return;
        setOrdersNonce((n) => n + 1);
        setTasksNonce((n) => n + 1);
      }),
    [],
  );

  // ── Per row ───────────────────────────────────────────────────────────────
  const byRow = useMemo(() => {
    const out = {};
    for (const row of rows || []) {
      if (row?.id == null) continue;
      const schema = rowSchemaOf(row, fallback);
      // A reply can come from either of the patient's numbers (cell or home).
      const phones = rowPhonesNormalized(row);
      out[row.id] = {
        orderItems: orderState.status === READY ? orderItemsForRow(row, orderIndex, fallback) : [],
        hasPhone: phones.size > 0,
        unread: unreadForRow(row, smsState.bySchema[schema]),
        tasks: taskState.status === READY ? tasksForRow(row, taskState.bySchema[schema] || [], fallback) : [],
      };
    }
    return out;
  }, [rows, fallback, orderState.status, orderIndex, smsState.bySchema, taskState]);

  return {
    byRow,
    status: { orders: orderState.status, messages: smsState.status, tasks: taskState.status },
    refreshOrders: () => setOrdersNonce((n) => n + 1),
    refreshMessages: () => setSmsNonce((n) => n + 1),
    refreshTasks: () => setTasksNonce((n) => n + 1),
  };
}
